import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/AdminLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { API_BASE_URL } from '../config/api';
import { Users, Mail, ChevronDown, ChevronUp, BookOpen, Send, UserCheck, ClipboardList, Download } from 'lucide-react';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('access_token')}` });

const DIALOGUE_TITLES = [
  ['01', 'Enterprise Resilience 2030'], ['02', 'Supply Chain Resilience'], ['03', 'ESG Beyond Compliance'],
  ['04', 'Stabilising MSMEs'], ['05', 'Climate Volatility as Enterprise Risk'], ['06', 'AI, Automation & Institutional Risk Governance'],
  ['07', 'Regional Enterprise Ecosystems'], ['08', 'Corporate Strategy in an Era of Fiscal Tightening'],
  ['09', 'Circular Economy as Competitive Strategy'], ['10', 'CSR & Institutional Capital']
];

const WAVES = [
  { audience: 'members', label: '1. All Members', desc: 'Members are intimated first (25% member fee shown)' },
  { audience: 'interested', label: '2. Interested Non-Members', desc: 'Includes membership pitch — auto-sends when early-bird ends' },
  { audience: 'public', label: '3. All Other Contacts', desc: 'Everyone else who has contacted CSED — auto-sends when early-bird ends' }
];

const fmtDate = (iso) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export default function AdminDialogues() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [forms, setForms] = useState({});
  const [meta, setMeta] = useState({});
  const [interest, setInterest] = useState({});
  const [totals, setTotals] = useState({ interested: 0, converted: 0 });
  const [attendees, setAttendees] = useState({});
  const [attNo, setAttNo] = useState(null);
  const [papers, setPapers] = useState([]);
  const [matSel, setMatSel] = useState({});
  const [savingNo, setSavingNo] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [expandedNo, setExpandedNo] = useState(null);
  const [commsNo, setCommsNo] = useState(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    if (user.role !== 'admin') { navigate('/'); return; }
    fetchDialogues();
    fetchInterest();
    fetchPapers();
  }, [user, authLoading]);

  const fetchDialogues = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/events/dialogues/all`);
      const map = {};
      const metaMap = {};
      const sel = {};
      DIALOGUE_TITLES.forEach(([no]) => {
        const d = data.dialogues.find(x => x.no === no) || {};
        map[no] = { date: d.date || '', venue: d.venue || '', registration_fee: d.registration_fee ?? '', open_registration: !!d.open_registration, early_bird_days: d.early_bird_days ?? '' };
        metaMap[no] = d;
        sel[no] = (d.materials || []).map(m => m.id);
      });
      setForms(map);
      setMeta(metaMap);
      setMatSel(sel);
    } catch (e) { console.error(e); }
  };

  const fetchInterest = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/events/admin/dialogue-interest`, { headers: authHeaders(), withCredentials: true });
      setInterest(data.interest || {});
      setTotals(data.totals || { interested: 0, converted: 0 });
    } catch (e) { console.error(e); }
  };

  const fetchPapers = async () => {
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/papers/?limit=100`);
      setPapers((data || []).filter(p => !p.status || ['active', 'published'].includes(p.status)));
    } catch (e) { console.error(e); }
  };

  const setField = (no, field, value) => setForms(prev => ({ ...prev, [no]: { ...prev[no], [field]: value } }));

  const save = async (no, title) => {
    const f = forms[no];
    setSavingNo(no);
    try {
      await axios.post(`${API_BASE_URL}/api/events/admin/dialogues`, {
        no, title,
        date: f.date || null,
        venue: f.venue || null,
        registration_fee: f.registration_fee === '' ? null : Number(f.registration_fee),
        open_registration: f.open_registration,
        early_bird_days: (f.early_bird_days === '' || f.early_bird_days == null) ? null : Number(f.early_bird_days)
      }, { headers: authHeaders(), withCredentials: true });
      await fetchDialogues();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingNo(null);
    }
  };

  const notify = async (no, audience, label) => {
    if (!window.confirm(`Send "${label}" intimation for Dialogue ${no}?`)) return;
    setBusyKey(`${no}-${audience}`);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/events/admin/dialogues/${no}/notify`,
        { audience }, { headers: authHeaders(), withCredentials: true });
      alert(data.message);
      await fetchDialogues();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to send intimation');
    } finally {
      setBusyKey(null);
    }
  };

  const toggleMaterial = (no, paperId) => {
    setMatSel(prev => {
      const cur = prev[no] || [];
      return { ...prev, [no]: cur.includes(paperId) ? cur.filter(x => x !== paperId) : [...cur, paperId] };
    });
  };

  const saveMaterials = async (no) => {
    setBusyKey(`${no}-mat-save`);
    try {
      await axios.post(`${API_BASE_URL}/api/events/admin/dialogues/${no}/materials`,
        { paper_ids: matSel[no] || [] }, { headers: authHeaders(), withCredentials: true });
      await fetchDialogues();
      alert('Reading materials saved');
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to save materials');
    } finally {
      setBusyKey(null);
    }
  };

  const sendMaterials = async (no) => {
    if (!window.confirm(`Email the curated reading material to interested MEMBERS for Dialogue ${no}?`)) return;
    setBusyKey(`${no}-mat-send`);
    try {
      const { data } = await axios.post(`${API_BASE_URL}/api/events/admin/dialogues/${no}/send-materials`,
        {}, { headers: authHeaders(), withCredentials: true });
      alert(data.message);
      await fetchDialogues();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to send materials');
    } finally {
      setBusyKey(null);
    }
  };

  const loadAttendees = async (no) => {
    if (attNo === no) { setAttNo(null); return; }
    setAttNo(no);
    try {
      const { data } = await axios.get(`${API_BASE_URL}/api/events/admin/dialogues/${no}/attendees`, { headers: authHeaders(), withCredentials: true });
      setAttendees(prev => ({ ...prev, [no]: data }));
    } catch (e) { console.error(e); }
  };

  const downloadCsv = (no) => {
    const data = attendees[no];
    if (!data?.attendees?.length) return;
    const rows = [['Name', 'Email', 'Phone', 'Amount (Rs)', 'Member', 'Paid At']];
    data.attendees.forEach(a => rows.push([a.name, a.email, a.phone, a.amount, a.is_member ? 'Yes' : 'No', a.paid_at ? new Date(a.paid_at).toLocaleString('en-IN') : '']));
    const csv = rows.map(r => r.map(c => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `dialogue-${no}-attendees.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const totalInterest = Object.values(interest).reduce((s, v) => s + (v?.count || 0), 0);

  return (
    <AdminLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="admin-dialogues-title">Leadership Dialogues</h1>
        <p className="text-gray-600">Announce dates & venues. Toggling "Open Registration" creates a payable event automatically (fee in ₹, members auto-pay 25% less).</p>
        <div className="mt-3 flex flex-wrap gap-2">
          <div className="inline-flex items-center gap-2 bg-[#1E3A5F]/5 border border-[#1E3A5F]/10 rounded-lg px-4 py-2" data-testid="total-interest-summary">
            <Users className="h-4 w-4 text-[#E87722]" />
            <span className="text-sm text-[#1E3A5F] font-medium">{totalInterest} total interest submission{totalInterest === 1 ? '' : 's'} across all Dialogues</span>
          </div>
          <div className="inline-flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2" data-testid="conversion-summary">
            <UserCheck className="h-4 w-4 text-green-600" />
            <span className="text-sm text-green-800 font-medium">
              {totals.converted} converted to member{totals.converted === 1 ? '' : 's'}
              {totals.interested > 0 ? ` (${Math.round((totals.converted / totals.interested) * 100)}% conversion)` : ''}
            </span>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {DIALOGUE_TITLES.map(([no, title]) => {
          const f = forms[no] || {};
          const int = interest[no];
          const m = meta[no] || {};
          const expanded = expandedNo === no;
          const commsOpen = commsNo === no;
          const notifyLog = m.notify_log || {};
          return (
            <Card key={no} className="p-5" data-testid={`admin-dialogue-card-${no}`}>
              <div className="flex items-center justify-between mb-3">
                <p className="font-bold text-[#1E3A5F]"><span className="text-[#E87722] mr-2">{no}</span>{title}</p>
                <div className="flex items-center gap-2">
                  {f.open_registration && <Badge className="bg-green-100 text-green-800">Registration Open</Badge>}
                  <button
                    onClick={() => setExpandedNo(expanded ? null : no)}
                    className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 transition-colors ${int?.count ? 'bg-[#E87722]/10 text-[#E87722] hover:bg-[#E87722]/20' : 'bg-gray-100 text-gray-500'}`}
                    data-testid={`dialogue-interest-count-${no}`}>
                    <Users className="h-3 w-3" />{int?.count || 0} interested{int?.converted_count ? ` · ${int.converted_count} converted` : ''}
                    {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                  </button>
                </div>
              </div>

              {expanded && (
                <div className="mb-3 border rounded-lg bg-gray-50 max-h-48 overflow-y-auto" data-testid={`dialogue-interest-list-${no}`}>
                  {int?.people?.length ? int.people.map((p, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 border-b last:border-b-0 text-sm">
                      <span className="font-medium text-gray-800 truncate">{p.name || '—'}</span>
                      <span className="flex items-center gap-2 flex-shrink-0">
                        <span className="text-gray-500">{p.email}</span>
                        {p.converted ? (
                          <Badge className="bg-green-100 text-green-800 text-[10px]" data-testid={`converted-tag-${no}-${i}`}>Converted ✓</Badge>
                        ) : p.is_member ? (
                          <Badge className="bg-blue-100 text-blue-800 text-[10px]">Member</Badge>
                        ) : null}
                      </span>
                    </div>
                  )) : (
                    <p className="px-3 py-3 text-sm text-gray-500">No interest submissions yet for this Dialogue.</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-3">
                <Input type="date" value={f.date || ''} onChange={(e) => setField(no, 'date', e.target.value)} data-testid={`dialogue-date-${no}`} />
                <Input placeholder="Venue" value={f.venue || ''} onChange={(e) => setField(no, 'venue', e.target.value)} data-testid={`dialogue-venue-${no}`} />
                <Input type="number" min="0" placeholder="Fee ₹" value={f.registration_fee ?? ''} onChange={(e) => setField(no, 'registration_fee', e.target.value)} data-testid={`dialogue-fee-${no}`} />
                <Input type="number" min="0" placeholder="Early-bird days" title="Members-only window (days)" value={f.early_bird_days ?? ''} onChange={(e) => setField(no, 'early_bird_days', e.target.value)} data-testid={`dialogue-earlybird-days-${no}`} />
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1 mb-2">
                {f.registration_fee && Number(f.registration_fee) > 0 ? (
                  <p className="text-xs text-green-700">Member fee (auto 25% off): ₹{Math.round(Number(f.registration_fee) * 0.75).toLocaleString('en-IN')}</p>
                ) : null}
                {m.early_bird_until && new Date(m.early_bird_until) > new Date() ? (
                  <p className="text-xs text-amber-700 font-medium" data-testid={`dialogue-earlybird-active-${no}`}>
                    Members-only until {fmtDate(m.early_bird_until)} — waves 2 & 3 auto-send after
                  </p>
                ) : null}
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <Switch checked={!!f.open_registration} onCheckedChange={(v) => setField(no, 'open_registration', v)} data-testid={`dialogue-open-toggle-${no}`} />
                  Open Registration
                </label>
                <div className="flex items-center gap-2">
                  {f.open_registration && (
                    <Button size="sm" variant="outline" onClick={() => loadAttendees(no)}
                      className="border-gray-400 text-gray-700" data-testid={`dialogue-attendees-toggle-${no}`}>
                      <ClipboardList className="h-3.5 w-3.5 mr-1.5" />
                      Attendees {attNo === no ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                    </Button>
                  )}
                  {f.open_registration && (
                    <Button size="sm" variant="outline" onClick={() => setCommsNo(commsOpen ? null : no)}
                      className="border-[#1E3A5F] text-[#1E3A5F] hover:bg-[#1E3A5F] hover:text-white" data-testid={`dialogue-comms-toggle-${no}`}>
                      <Mail className="h-3.5 w-3.5 mr-1.5" />
                      Communications {commsOpen ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}
                    </Button>
                  )}
                  <Button size="sm" onClick={() => save(no, title)} disabled={savingNo === no}
                    className="bg-[#E87722] hover:bg-[#d66a1a]" data-testid={`dialogue-save-${no}`}>
                    {savingNo === no ? 'Saving...' : 'Save'}
                  </Button>
                </div>
              </div>

              {attNo === no && f.open_registration && (
                <div className="mt-4 border-t pt-4" data-testid={`dialogue-attendees-panel-${no}`}>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-semibold text-[#1E3A5F]">
                      Paid Attendees
                      {attendees[no]?.summary ? (
                        <span className="font-normal text-gray-500 ml-2 text-xs">
                          {attendees[no].summary.total} total · {attendees[no].summary.members} member{attendees[no].summary.members === 1 ? '' : 's'} · {attendees[no].summary.non_members} non-member{attendees[no].summary.non_members === 1 ? '' : 's'} · ₹{attendees[no].summary.revenue.toLocaleString('en-IN')} collected
                        </span>
                      ) : null}
                    </p>
                    <Button size="sm" variant="outline" onClick={() => downloadCsv(no)}
                      disabled={!(attendees[no]?.attendees?.length)} data-testid={`dialogue-attendees-csv-${no}`}>
                      <Download className="h-3 w-3 mr-1" />CSV
                    </Button>
                  </div>
                  <div className="border rounded-lg bg-gray-50 max-h-56 overflow-y-auto">
                    {attendees[no]?.attendees?.length ? attendees[no].attendees.map((a, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 border-b last:border-b-0 text-sm">
                        <div className="min-w-0">
                          <p className="font-medium text-gray-800 truncate">{a.name || '—'}</p>
                          <p className="text-xs text-gray-500 truncate">{a.email}{a.phone ? ` · ${a.phone}` : ''}</p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <span className="text-gray-700 font-medium">₹{Number(a.amount).toLocaleString('en-IN')}</span>
                          <Badge className={a.is_member ? 'bg-blue-100 text-blue-800 text-[10px]' : 'bg-gray-200 text-gray-700 text-[10px]'}>
                            {a.is_member ? 'Member' : 'Non-member'}
                          </Badge>
                        </div>
                      </div>
                    )) : (
                      <p className="px-3 py-3 text-sm text-gray-500">No paid registrations yet for this Dialogue.</p>
                    )}
                  </div>
                </div>
              )}

              {commsOpen && f.open_registration && (
                <div className="mt-4 border-t pt-4 space-y-4" data-testid={`dialogue-comms-panel-${no}`}>
                  <div>
                    <p className="text-sm font-semibold text-[#1E3A5F] mb-2">Staged Intimation (send in order, days apart)</p>
                    <div className="space-y-2">
                      {WAVES.map(w => {
                        const log = notifyLog[w.audience];
                        return (
                          <div key={w.audience} className="flex items-center justify-between gap-2 bg-gray-50 border rounded-lg px-3 py-2">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-gray-800">{w.label}</p>
                              <p className="text-xs text-gray-500">{log ? `Sent ${fmtDate(log.sent_at)} to ${log.count}${log.auto ? ' (auto)' : ''}` : w.desc}</p>
                            </div>
                            <Button size="sm" variant={log ? 'outline' : 'default'}
                              onClick={() => notify(no, w.audience, w.label)}
                              disabled={busyKey === `${no}-${w.audience}`}
                              className={log ? 'border-gray-300 text-gray-600' : 'bg-[#1E3A5F] hover:bg-[#16304f] text-white'}
                              data-testid={`dialogue-notify-${w.audience}-${no}`}>
                              <Send className="h-3 w-3 mr-1" />
                              {busyKey === `${no}-${w.audience}` ? 'Sending...' : (log ? 'Re-send' : 'Send')}
                            </Button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-semibold text-[#1E3A5F] mb-1 flex items-center gap-1.5">
                      <BookOpen className="h-4 w-4 text-[#E87722]" />Curated Reading Material (for interested members)
                    </p>
                    {papers.length ? (
                      <div className="border rounded-lg max-h-40 overflow-y-auto mb-2" data-testid={`dialogue-materials-list-${no}`}>
                        {papers.map(p => (
                          <label key={p.id} className="flex items-center gap-2 px-3 py-1.5 border-b last:border-b-0 text-sm cursor-pointer hover:bg-gray-50">
                            <input type="checkbox" checked={(matSel[no] || []).includes(p.id)}
                              onChange={() => toggleMaterial(no, p.id)}
                              className="accent-[#E87722]" data-testid={`material-check-${no}-${p.id}`} />
                            <span className="truncate text-gray-700">{p.title}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 mb-2">No published papers in the Research Library yet.</p>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button size="sm" variant="outline" onClick={() => saveMaterials(no)} disabled={busyKey === `${no}-mat-save`}
                        data-testid={`dialogue-materials-save-${no}`}>
                        {busyKey === `${no}-mat-save` ? 'Saving...' : `Save Selection (${(matSel[no] || []).length})`}
                      </Button>
                      <Button size="sm" onClick={() => sendMaterials(no)}
                        disabled={busyKey === `${no}-mat-send` || !(m.materials || []).length}
                        className="bg-[#E87722] hover:bg-[#d66a1a]" data-testid={`dialogue-materials-send-${no}`}>
                        <Send className="h-3 w-3 mr-1" />
                        {busyKey === `${no}-mat-send` ? 'Sending...' : 'Email to Interested Members'}
                      </Button>
                    </div>
                    {m.materials_sent_at && (
                      <p className="text-xs text-gray-400 mt-1.5" data-testid={`dialogue-materials-last-sent-${no}`}>
                        Materials last sent {fmtDate(m.materials_sent_at)} to {m.materials_sent_count} member(s)
                      </p>
                    )}
                  </div>
                </div>
              )}
            </Card>
          );
        })}
      </div>
    </AdminLayout>
  );
}
